import type { SortKey, SourcePostFilters } from './types.ts';
import { preferencjeRankingu } from './predykatyMiekkie.ts';
import {
  KOLUMNY_RANKINGU,
  KOLUMNY_SORTOWANIA_PINOW,
  PIN_COLUMNS,
  SERP_COLUMNS,
  applySerpFilters,
  applySerpSort,
  hasPolygon,
  kolumnyPinow,
  sciezkaOdczytu,
  type SerpFilterableQuery,
} from './serpQuery.ts';

/**
 * ZŁOŻENIE ZAPYTANIA SERP-u — ta sama funkcja dla strony wyników i dla `send-filter-alerts`
 * (plan §1.68). Klienta Supabase PODAJE WYWOŁUJĄCY (aplikacja: `@/integrations/supabase/client`;
 * funkcja brzegowa: `createClient` z `npm:@supabase/supabase-js@2`) — moduł nie tworzy go i nie
 * importuje, żeby pozostać czysty i zgodny z Deno. Typ klienta jest STRUKTURALNY (jak
 * `SerpFilterableQuery`): tyle z `PostgrestClient`, ile ścieżka odczytu naprawdę woła.
 */

/** Zapytanie po `select()` — łańcuch filtrów/sortowania plus to, co woła stronicowanie i licznik. */
export interface SerpQuery extends SerpFilterableQuery {
  limit(n: number): this;
  range(from: number, to: number): this;
  then<T>(onfulfilled: (value: SerpResponse) => T | PromiseLike<T>): PromiseLike<T>;
}

export interface SerpResponse {
  data: unknown[] | null;
  error: { message: string } | null;
  count: number | null;
}

export interface SerpCountOpts {
  count: 'exact';
  head?: boolean;
}

/** `klient.schema('flat')` — tabela `offers` i dwie funkcje: `offers_ranked`, `offers_in_polygon`. */
export interface SerpSchemaClient {
  from(table: string): { select(cols: string, opts?: SerpCountOpts): SerpQuery };
  rpc(fn: string, args: Record<string, unknown>, opts?: Partial<SerpCountOpts>): SerpQuery & {
    select(cols: string): SerpQuery;
  };
}

export interface SerpClient {
  schema(name: string): SerpSchemaClient;
}

// All SERP reads (list, pins, count, slider bounds, single-row popup) go through
// the SERVING LAYER table `flat.offers` — one row per offer, already deduped and
// owner-merged by flat.project_offers(). It replaces the old
// serp_cluster_heads(_m) matview outright ([R10]: 0.83 ms on the table vs 45.7 ms
// on the matview, because there is no per-query dedup/join left to precompute).
// The matview is abandoned, not rebuilt — see docs/architecture/offers-etl-model.md.
export const SERP_TABLE = 'offers';

/** GeoJSON Polygon geometry from [lat,lon] vertices (lon,lat order; ring closed). */
export function polygonToGeoJSON(verts: [number, number][]) {
  const ring: [number, number][] = verts.map(([lat, lon]) => [lon, lat]);
  if (ring.length > 0) {
    const [fx, fy] = ring[0];
    const [lx, ly] = ring[ring.length - 1];
    if (fx !== lx || fy !== ly) ring.push([fx, fy]);
  }
  return { type: 'Polygon', coordinates: [ring] };
}

/**
 * LICZNIK NA ŚCIEŻCE FUNKCJI — bez pełnych wierszy (pomiar 28.09).
 *
 * Na tabeli licznik to HEAD: zero bajtów ciała. Na funkcji z argumentem-OBIEKTEM (`p_prefs`,
 * `p_geojson`) postgrest-js zamienia HEAD na POST z `Prefer: return=minimal`, a PostgREST dla
 * funkcji tego nie honoruje i oddaje CAŁY wynik bez `select`: zmierzone 4 167 KB i 871 ms na
 * jedno pytanie o liczbę (offers_ranked, wszystkie ~7 400 wierszy z opisem i zdjęciami). Liczymy
 * więc zwykłym POST-em z `count=exact`, `select=id` i `limit=1` — nagłówek Content-Range niesie
 * pełną liczbę, ciało jeden identyfikator. Filtry z łańcucha działają jak dotąd.
 */
function licznikFunkcji(schema: SerpSchemaClient, funkcja: string, args: Record<string, unknown>): SerpQuery {
  return schema
    .rpc(funkcja, args, { count: 'exact' })
    .select('id')
    .limit(1);
}

/**
 * Base SERP read with columns selected: through flat.offers_in_polygon (geog ∈
 * the drawn polygon, RETURNS SETOF flat.offers) when a draw area is active, else
 * straight from flat.offers. Either way the caller layers the SAME
 * applySerpFilters/applySerpSort/.range()/count chain — PostgREST supports the
 * full filter chain on a SETOF-returning function exactly as on a table.
 */
export function serpSelect(
  klient: SerpClient,
  filters: SourcePostFilters,
  cols: string,
  count?: SerpCountOpts,
): SerpQuery {
  const schema = klient.schema('flat');
  const tbl = schema.from(SERP_TABLE);
  const sciezka = sciezkaOdczytu(filters);
  if (sciezka === 'ranking') {
    // FILTRY MIĘKKIE (1b cz. 2): `flat.offers_ranked` ocenia każdą aktywną ofertę względem
    // `p_prefs` (ten sam obiekt co histogram) i zwraca `dopasowanie_klucz`; resztę filtrów i
    // stronicowanie kładzie ten sam łańcuch co na tabeli. Narysowany obszar idzie jako
    // `p_geojson` — funkcja zwraca prawdziwe kolumny, więc nie ma tu wariantu przez `extras`.
    // `.select()` jak niżej przy obszarze: bez niego wróciłyby prawdziwe nazwy kolumn zamiast
    // aliasów `extracted_*`. Klucz i dwie kolumny adnotacji dokłada `KOLUMNY_RANKINGU`.
    const rpc = schema.rpc(
      'offers_ranked',
      {
        p_prefs: preferencjeRankingu(filters),
        p_geojson: hasPolygon(filters) ? polygonToGeoJSON(filters.polygon!) : null,
      },
      count ?? {},
    );
    if (count?.head) return licznikFunkcji(schema, 'offers_ranked', {
      p_prefs: preferencjeRankingu(filters),
      p_geojson: hasPolygon(filters) ? polygonToGeoJSON(filters.polygon!) : null,
    });
    // Piny nie rysują adnotacji karty — dostają sam klucz, po którym sortujemy (PostgREST na
    // funkcji sortuje po WYBRANYCH kolumnach). Pomiar 28.09: 457 KB → patrz commit.
    const dodatek = cols === PIN_COLUMNS ? KOLUMNY_SORTOWANIA_PINOW + ',dopasowanie_klucz' : KOLUMNY_RANKINGU;
    return rpc.select(cols + dodatek);
  }
  if (sciezka === 'obszar') {
    // RPC returns SETOF flat.offers; cast to the same builder shape as the table
    // read so callers chain filters/sort/range/count identically.
    const rpc = schema.rpc(
      'offers_in_polygon',
      { p_geojson: polygonToGeoJSON(filters.polygon!) },
      count ?? {},
    );
    // `.select(cols)` is NOT optional here, and dropping it was a real bug: a
    // bare rpc() returns the function's own row shape, i.e. flat.offers' REAL
    // column names. Every component reads the client-side `extracted_*` names,
    // which exist only as PostgREST output aliases inside `cols` -- so with a
    // polygon drawn, extracted_rent_pln/rooms/area_m2/city/suburb/lat/lon/extras
    // all came back undefined and the cards rendered "Cena na zapytanie" with no
    // location and no title, while the same offer's detail page (a table read)
    // was complete. postgrest-js types select() for Method 'RPC' precisely so
    // this works; it also trims the payload from all 54 columns to the ~30 the
    // SERP renders (5 for pins).
    // Skipped for head-only counts: select() appends `Prefer: return=representation`,
    // which has no meaning on a HEAD request that returns no body.
    // HOTFIX 27.09: `flat.offers_in_polygon` ma jawną listę kolumn w RETURNS TABLE i nie
    // zwraca `klasa_jakosci` (krok O dodał ją tylko do tabeli). Wybranie jej przez RPC dawało
    // „column pgrst_call.klasa_jakosci does not exist” i zerowało rysowanie obszaru. Do czasu
    // migracji dokładającej kolumnę do funkcji obszar nie sortuje po klasie.
    const colsObszaru = (cols === PIN_COLUMNS ? kolumnyPinow('obszar') : cols).replace(/,klasa_jakosci\b/, '');
    if (count?.head) return licznikFunkcji(schema, 'offers_in_polygon', {
      p_geojson: polygonToGeoJSON(filters.polygon!),
    });
    return rpc.select(colsObszaru);
  }
  return count ? tbl.select(cols, count) : tbl.select(cols);
}

/**
 * ZAPYTANIE O LISTĘ — dokładnie to, co składa strona wyników przed `.range()`: wybór ścieżki
 * (tabela / obszar / ranking), kolumny karty, łańcuch filtrów i sortowanie. Wywołujący dokłada
 * tylko stronę (`.range(od, do)`); `send-filter-alerts` dołoży jeszcze „nowe od ostatniego
 * alertu” i weźmie pierwsze 15 (docs/AlertEmails.md §2) — poza tym nic.
 *
 * `sort` domyślnie bierze się z zestawu (`filters.sort`), jak na stronie wyników.
 */
export function buildQuery(
  klient: SerpClient,
  filters: SourcePostFilters,
  sort: SortKey | undefined = filters.sort,
  opcje?: { kolumny?: string; count?: SerpCountOpts },
): SerpQuery {
  const q = serpSelect(klient, filters, opcje?.kolumny ?? SERP_COLUMNS, opcje?.count);
  return applySerpSort(applySerpFilters(q, filters), sort, filters.costMode, sciezkaOdczytu(filters));
}
